import { IonContent, IonPage, IonSpinner } from "@ionic/react";
import { Redirect, matchPath, useLocation } from "react-router-dom";
import { Routes } from "@/lib/router";

type RouteGuardProps = React.PropsWithChildren<{
  /** Whether the visitor is a signed-in employee or admin. Resolved once, in `App`. */
  isAuthenticated: boolean;
  /** `false` for the routes that only exist while signed out — the auth stack. */
  required?: boolean;
  /** Where a signed-in visitor to a signed-out route goes instead. */
  authPath?: string;
  /** Where a signed-out visitor to a protected route goes instead. */
  unauthPath?: string;
}>;

/**
 * De auth-routes die ook moeten renderen voor iemand die al is ingelogd.
 *
 * De hele auth-stack hangt onder een `RouteGuard` met `required={false}`, en die stuurde tot nu toe
 * iedere ingelogde bezoeker door naar `/app`. Voor inloggen en registreren klopt dat: daar heb je
 * niets te zoeken als je er al doorheen bent. Voor deze twee paden niet.
 *
 * `accept-invitation/:token` kent de tak `ACCOUNT_EXISTS_PASSWORD_REQUIRED`, het geval uit "een
 * mens, een login": dit adres heeft al een account, dus de uitnodiging voegt een lidmaatschap toe
 * in plaats van er een aan te maken. Dat scherm is dus juist gebouwd voor iemand die is ingelogd,
 * en de router liet precies die persoon er niet komen. Een klant meldde het als "de deeplink opent
 * de app en ik land op home".
 *
 * `reset-password` heeft dezelfde bug, met een scherper gevolg: het token zit in de querystring en
 * die gaat bij de redirect verloren. Wie op een telefoon met een lopende sessie op de link in zijn
 * mail tikt, kan zijn wachtwoord niet zetten.
 *
 * Waarom hier en niet met een extra `Route` in `global.router.tsx`: `IonRouterOutlet` is geen
 * `Switch`. Zijn `matchRoute` loopt alle kinderen langs en houdt de laatste match over, dus twee
 * `AuthRouter`-instanties in een outlet leveren precies de witte schermen op die in c90d653 zijn
 * opgeruimd. De beslissing hoort op de plek waar hij genomen wordt.
 */
const rendertOokIngelogd = (pathname: string) =>
  [Routes.AuthAcceptInvite.pattern, Routes.AuthForgotPasswordReset.pattern].some(
    (pattern) => matchPath(pathname, { path: pattern }) !== null,
  );

/**
 * The auth check for one route, as a component instead of a wrapper the router builds.
 *
 * This used to be `requireAuth(Page)`: a factory called inside the router's render and handed to
 * `<Route component={…}>`. React Router calls `createElement` on whatever `component` holds, so a
 * factory that returns a fresh function every render hands React a new component _type_ every
 * render — and React answers a new type the only way it can, by unmounting the old tree and
 * mounting a new one. Under `/app` that tree is `IonTabs` and a nested `IonRouterOutlet`, so every
 * re-render of the global router threw Ionic's whole page stack away mid-flight and built another.
 *
 * That is where the white screens came from. `StackManager` enters a page by adding
 * `ion-page-invisible` and only clears it once `routerOutlet.commit()` resolves; a commit whose
 * outlet is torn down underneath it never resolves. The `.ion-page` stays in the DOM at `opacity:
 * 0`, filling the viewport and swallowing every tap — white, and frozen.
 *
 * A component declared once at module scope keeps its identity across renders, so React updates the
 * existing tree instead of replacing it. The decision itself is unchanged.
 */
export function RouteGuard({
  isAuthenticated,
  required = true,
  authPath,
  unauthPath,
  children,
}: RouteGuardProps) {
  const location = useLocation();

  if (required && !isAuthenticated) {
    // Already on the sign-in screen: redirecting there again would be a loop, so hold instead.
    if (location.pathname === Routes.AuthSignInPassword.pattern) {
      return <RouteGuardPending />;
    }

    const signInUrl =
      unauthPath ??
      Routes.AuthSignInPassword(undefined, {
        search: { callbackUrl: location.pathname },
      });

    return <Redirect to={signInUrl} />;
  }

  if (!required && isAuthenticated) {
    if (rendertOokIngelogd(location.pathname)) {
      return <>{children}</>;
    }

    return <Redirect to={authPath ?? Routes.App()} />;
  }

  return <>{children}</>;
}

/**
 * A spinner the router outlet can actually transition to.
 *
 * Anything an `IonRouterOutlet` route renders has to carry an `ion-page`, because that element is
 * what `StackManager` registers and animates. A bare `<div>` here leaves the outlet with a view it
 * can never enter, which is the same blank screen by another road.
 */
/**
 * Een route die nog niets te zeggen heeft.
 *
 * Moet een `ion-page` zijn: `StackManager` registreert alleen pagina's en zonder die klasse blijft
 * de outlet halverwege zijn transitie hangen -- het witte scherm uit docs/common-issues.md.
 */
export function RouteGuardPending() {
  return (
    <IonPage>
      <IonContent>
        <div className="flex h-full w-full items-center justify-center">
          <IonSpinner />
        </div>
      </IonContent>
    </IonPage>
  );
}
